import React, { useEffect, useState } from "react";
import { dummyStudents } from "../../data/dummyStudents";
import { chartOfAccounts as baseCoA } from "../../data/chartOfAccounts";
import { db } from "../../services/firebase";
import { collection, onSnapshot, doc, setDoc, deleteDoc, getDocs } from "firebase/firestore";

const initialAccounts = [
  // Seed from baseCoA
  ...baseCoA.map(a => ({
    id: Number(a.code),
    name: a.name,
    type: a.type === "Revenue" ? "Revenue" : a.type,
    parent: ["Asset","Liability","Equity"].includes(a.type) ? a.type : (a.type === "Revenue" ? "Income" : "Operating Expenses"),
    description: a.description || "",
    campus: ""
  })),
  // Additional school-specific accounts
  { id: 1010, name: "Cash in Bank - BDO", type: "Asset", parent: "Asset", description: "Checking account", campus: "" },
  { id: 1020, name: "Petty Cash", type: "Asset", parent: "Asset", description: "Small cash fund", campus: "" },
  { id: 1110, name: "Accounts Receivable - Tuition", type: "Asset", parent: "Asset", description: "Unpaid tuition fees", campus: "" },
  { id: 1120, name: "Accounts Receivable - Misc", type: "Asset", parent: "Asset", description: "Unpaid miscellaneous fees", campus: "" },
  { id: 2110, name: "Unearned Tuition Revenue", type: "Liability", parent: "Liability", description: "Advanced payments", campus: "" },
  { id: 3110, name: "Scholarship Discounts", type: "Revenue", parent: "Income", description: "Contra-revenue for discounts", campus: "" },
  { id: 3200, name: "Registration Fee Income", type: "Revenue", parent: "Income", description: "Enrollment registration fees", campus: "" },
  { id: 3210, name: "Books & Materials Sales", type: "Revenue", parent: "Income", description: "Learning materials", campus: "" },
  { id: 4100, name: "Office Supplies", type: "Expense", parent: "Operating Expenses", description: "Teaching and non-teaching staff", campus: "" },
  { id: 4110, name: "Employee Benefits", type: "Expense", parent: "Operating Expenses", description: "SSS/PhilHealth/Pag-IBIG", campus: "" },
  { id: 4200, name: "Utilities", type: "Expense", parent: "Operating Expenses", description: "Electricity, water", campus: "" },
  { id: 4210, name: "Internet and Communications", type: "Expense", parent: "Operating Expenses", description: "Internet plans, phone", campus: "" },
  { id: 4300, name: "Rent", type: "Expense", parent: "Operating Expenses", description: "Building/room rentals", campus: "" },
  { id: 4310, name: "Repairs and Maintenance", type: "Expense", parent: "Operating Expenses", description: "Facilities and equipment", campus: "" },
  { id: 4320, name: "Depreciation", type: "Expense", parent: "Operating Expenses", description: "Fixed assets", campus: "" },
  { id: 4330, name: "Supplies", type: "Expense", parent: "Operating Expenses", description: "Office and school supplies", campus: "" },
  { id: 4340, name: "Professional Fees", type: "Expense", parent: "Operating Expenses", description: "Legal, audit, consulting", campus: "" },
  { id: 4350, name: "Bank Charges", type: "Expense", parent: "Operating Expenses", description: "Service fees", campus: "" },
];

const accountTypes = ["Asset", "Liability", "Equity", "Income", "Revenue", "Expense"];

export default function ChartOfAccounts() {
  const [accounts, setAccounts] = useState(() => {
    try {
      const stored = localStorage.getItem("accounts");
      return stored ? JSON.parse(stored) : initialAccounts;
    } catch {
      return initialAccounts;
    }
  });
  const campuses = Array.from(new Set((dummyStudents || []).map(s => s.campus))).filter(Boolean);
  const [showForm, setShowForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState({
    name: "",
    type: "Asset",
    parent: "",
    description: "",
    campus: ""
  });

  const handleImportFromCloud = async () => {
    try {
      const snap = await getDocs(collection(db, "accounts"));
      const rows = snap.docs.map(d => {
        const data = d.data();
        return {
          id: Number(data.id ?? d.id),
          name: data.name || "",
          type: data.type || "Expense",
          parent: data.parent || "",
          description: data.description || "",
          campus: data.campus || "",
          code: data.code || String(data.id ?? d.id)
        };
      });
      if (rows.length) {
        const existing = new Map(accounts.map(a => [Number(a.id), a]));
        rows.forEach(r => existing.set(Number(r.id), r));
        const merged = Array.from(existing.values()).sort((a,b)=>String(a.name).localeCompare(String(b.name)));
        setAccounts(merged);
        try {
          localStorage.setItem("accounts", JSON.stringify(merged));
        } catch (err) {
          console.warn("persist failed");
        }
      }
    } catch (err) {
      console.warn("import failed");
    }
  };

  const handleSeedDefaults = () => {
    const existingKey = new Set(accounts.map(a => `${String(a.name).toLowerCase()}|${String(a.type).toLowerCase()}`));
    const existingIds = new Set(accounts.map(a => Number(a.id)));
    let maxId = accounts.length ? Math.max(...accounts.map(a => Number(a.id) || 0)) : 0;
    const toAdd = [];
    initialAccounts.forEach(a => {
      const key = `${String(a.name).toLowerCase()}|${String(a.type).toLowerCase()}`;
      if (!existingKey.has(key)) {
        let nextId = Number(a.id);
        if (existingIds.has(nextId) || !Number.isFinite(nextId)) {
          nextId = ++maxId;
        }
        existingIds.add(nextId);
        toAdd.push({ ...a, id: nextId });
      }
    });
    if (toAdd.length) {
      const merged = [...accounts, ...toAdd];
      setAccounts(merged);
      try {
        localStorage.setItem("accounts", JSON.stringify(merged));
      } catch (err) {
        console.warn("Failed to persist accounts");
      }
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem("accounts", JSON.stringify(accounts));
    } catch (err) {
      console.warn("Failed to persist accounts");
    }
    // Push to Firestore for org-wide availability
    (async () => {
      try {
        for (const acc of accounts) {
          const ref = doc(db, "accounts", String(acc.id || acc.code));
          await setDoc(ref, {
            id: Number(acc.id || acc.code),
            code: String(acc.code || acc.id || ""),
            name: acc.name,
            type: acc.type,
            parent: acc.parent || "",
            description: acc.description || "",
            campus: acc.campus || ""
          }, { merge: true });
        }
      } catch (e) { /* no-op */ }
    })();
  }, [accounts]);

  const handleOpenForm = (index = null) => {
    setEditIndex(index);
    if (index !== null) {
      setForm(accounts[index]);
    } else {
      setForm({ name: "", type: "Asset", parent: "", description: "", campus: "" });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditIndex(null);
    setForm({ name: "", type: "Asset", parent: "", description: "", campus: "" });
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (editIndex !== null) {
      // Edit existing
      const updated = [...accounts];
      updated[editIndex] = { ...form, id: accounts[editIndex].id };
      setAccounts(updated);
      localStorage.setItem("accounts", JSON.stringify(updated));
      try {
        await setDoc(doc(db, "accounts", String(updated[editIndex].id)), { ...updated[editIndex], code: String(updated[editIndex].code || updated[editIndex].id) }, { merge: true });
      } catch (e) { /* ignore */ }
    } else {
      // Add new
      const next = [
        ...accounts,
        { ...form, id: accounts.length ? Math.max(...accounts.map(a => a.id)) + 1 : 1 }
      ];
      setAccounts(next);
      localStorage.setItem("accounts", JSON.stringify(next));
      try {
        const acc = next[next.length - 1];
        await setDoc(doc(db, "accounts", String(acc.id)), { ...acc, code: String(acc.code || acc.id) }, { merge: true });
      } catch (e) { /* ignore */ }
    }
    handleCloseForm();
  };

  const handleDelete = async index => {
    const next = accounts.filter((_, i) => i !== index);
    setAccounts(next);
    try {
      localStorage.setItem("accounts", JSON.stringify(next));
    } catch (err) {
      console.warn("Failed to persist accounts");
    }
    try {
      await deleteDoc(doc(db, "accounts", String(accounts[index].id)));
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Chart of Accounts</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={handleImportFromCloud}>Import From Cloud</button>
          <button className="btn btn-outline-warning" onClick={handleSeedDefaults}>Load Standard Accounts</button>
          <button className="btn btn-warning" onClick={() => handleOpenForm()}>Add Account</button>
        </div>
      </div>
      {accounts.length < 5 && (
        <div className="alert alert-info">
          Only a few accounts found. Click “Load Standard Accounts” to add common school collections and expenses.
        </div>
      )}
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Account ID</th>
            <th>Account Name</th>
            <th>Account Type</th>
            <th>Parent Account</th>
            <th>Campus</th>
            <th>Description</th>
            <th style={{ width: 90 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((acc, idx) => (
            <tr key={acc.id}>
              <td>{acc.id}</td>
              <td>{acc.name}</td>
              <td>{acc.type}</td>
              <td>{acc.parent || "-"}</td>
              <td>{acc.campus || "All"}</td>
              <td>{acc.description}</td>
              <td>
                <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleOpenForm(idx)}>Edit</button>
                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(idx)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Modal/Form */}
      {showForm && (
        <div className="modal show" style={{
          display: "block",
          background: "rgba(0,0,0,0.3)",
        }}>
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">{editIndex !== null ? "Edit Account" : "Add Account"}</h5>
                <button type="button" className="btn-close" onClick={handleCloseForm}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Account Name</label>
                  <input type="text" className="form-control" name="name" value={form.name} onChange={handleChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Account Type</label>
                  <select className="form-select" name="type" value={form.type} onChange={handleChange} required>
                    {accountTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Parent Account</label>
                  <input type="text" className="form-control" name="parent" value={form.parent} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Campus</label>
                  <select className="form-select" name="campus" value={form.campus} onChange={handleChange}>
                    <option value="">All Campuses</option>
                    {campuses.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" name="description" value={form.description} onChange={handleChange} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseForm}>Cancel</button>
                <button type="submit" className="btn btn-warning">{editIndex !== null ? "Update" : "Add"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
